import { useMemo } from "react";
import { Autocomplete } from "@aclymatepackages/atoms";
import productPackagingMaterials from "@aclymatepackages/lists/productPackagingMaterials";
import productMaterials from "@aclymatepackages/lists/productMaterials";

import useProductsEmissionsFactors from "../../../helpers/contexts/ProductsEmissionsFactors";

const PACKAGING_GROUP = "Packaging";
const MATERIALS_GROUP = "Product materials (not packaging)";

// Product materials that are ordinary packaging materials (paper hangtags and
// tissue, textile string and strapping, plastic bags) even though the packaging
// list has no entry for them. They are offered, and selected, as packaging with
// no warning. Anything else (metals, for example) still shows the "not
// packaging" warning.
const PACKAGING_COMPATIBLE_MATERIAL_IDS = new Set([
  "paper",
  "recycled_paper",
  "cardboard",
  "polyester_textile",
  "rpet_textile",
  "nylon_textile",
  "pp_textile",
  "pp_plastic",
  "pe_plastic",
  "pet_plastic",
  "cotton"
]);

const normalizeReference = (reference) =>
  String(reference || "")
    .toLowerCase()
    .split(/-|\s/)
    .join("_");

const sortByName = (options) =>
  [...options].sort((a, b) => a.name.localeCompare(b.name));

const toOption = ({ id, label }, group) => ({
  id,
  name: label ?? String(id),
  group
});

const ProductPackagingMaterials = ({
  multiple = false,
  setValue,
  value,
  label = "Select packaging materials",
  helperText,
  ...otherProps
}) => {
  const factorsContext = useProductsEmissionsFactors();
  const emissionsFactors = factorsContext?.[0]?.emissionsFactors;

  // The footprint calc looks a row's type up in the packaging AND materials
  // factor tables together, so a product material (paper, zinc alloy...) is a
  // valid, calculable choice -- but it isn't real packaging, so it is listed
  // in its own group and flagged when chosen. Ids with no factor in either
  // table are never offered: they would calculate as 0 kg with no warning.
  // Until the factor tables load (or outside the provider) nothing is hidden.
  const options = useMemo(() => {
    const factorTables = [
      ...(emissionsFactors?.packaging || []),
      ...(emissionsFactors?.materials || [])
    ];
    const hasFactor = factorTables.length
      ? (id) =>
          factorTables.some(
            ({ reference }) => normalizeReference(reference) === id
          )
      : () => true;

    const packagingIds = new Set(productPackagingMaterials.map(({ id }) => id));
    const otherMaterials = productMaterials.filter(
      ({ id }) => !packagingIds.has(id) && hasFactor(id)
    );
    const isCompatible = ({ id }) => PACKAGING_COMPATIBLE_MATERIAL_IDS.has(id);
    return [
      ...sortByName([
        ...productPackagingMaterials
          .filter(({ id }) => hasFactor(id))
          .map((material) => toOption(material, PACKAGING_GROUP)),
        ...otherMaterials
          .filter(isCompatible)
          .map((material) => toOption(material, PACKAGING_GROUP))
      ]),
      ...sortByName(
        otherMaterials
          .filter((material) => !isCompatible(material))
          .map((material) => toOption(material, MATERIALS_GROUP))
      )
    ];
  }, [emissionsFactors]);

  const renderOption = (props, option) => (
    <li {...props} key={`${option.group}-${option.id}`}>
      {option?.name}
    </li>
  );

  // Map incoming value (id or array of ids) to option object(s) expected by Autocomplete
  const selectedValue = multiple
    ? Array.isArray(value)
      ? options.filter((o) => value.includes(o.id))
      : []
    : options.find((o) => o.id === value) || null;

  const selectedOptions = multiple ? selectedValue : [selectedValue];
  const nonPackagingNames = selectedOptions
    .filter((option) => option?.group === MATERIALS_GROUP)
    .map(({ name }) => name);

  // A stored id that isn't offered at all (typo, retired id, no factor) would
  // otherwise render as an empty box that silently counts as 0 kg.
  const hasUnrecognizedValue = multiple
    ? Array.isArray(value) && value.length > selectedValue.length
    : !!value && !selectedValue;

  const warningText = hasUnrecognizedValue
    ? "This material isn't recognized. Pick a valid packaging material."
    : nonPackagingNames.length
    ? `${nonPackagingNames.join(", ")} ${
        nonPackagingNames.length > 1 ? "are product materials" : "is a product material"
      }, not packaging. Confirm this is correct.`
    : null;

  const combinedHelperText = [warningText, helperText]
    .filter(Boolean)
    .join(" · ");

  const handleChange = (newValue) => {
    if (multiple) {
      const ids = Array.isArray(newValue) ? newValue.map((v) => v.id) : [];
      return setValue(ids);
    }

    return setValue(newValue ? newValue.id : null);
  };

  return (
    <Autocomplete
      label={label}
      availableOptions={options}
      multiple={multiple}
      renderOption={renderOption}
      groupBy={(option) => option.group}
      value={selectedValue}
      setValue={handleChange}
      isOptionEqualToValue={(option, current) => option.id === current?.id}
      error={hasUnrecognizedValue}
      helperText={combinedHelperText || undefined}
      {...otherProps}
    />
  );
};

export default ProductPackagingMaterials;
